import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Chip, Input, toast } from '@heroui/react';
import {
  ArrowRight,
  Building2,
  Check,
  ClipboardList,
  Eye,
  Pencil,
  Plus,
  Presentation,
  Search,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  MAX_SHEETS_PER_TANDEM,
  buildTable,
  isMaxPriority,
  summarizeTandems,
  type Alert,
  type BoardAlert,
  type TandemSheet,
  type TandemSummary,
} from '@odd/domain';
import { useDeleteTandemSheet, useSaveTandemSheet } from '../lib/queries';
import { useErpLinks } from '../lib/erp';
import { makeMatcher } from '../lib/search';
import type { DashboardLayout } from '../lib/layout';
import { useBoardCtx } from './BoardContext';
import { ManualCaseSheet } from './ManualCaseSheet';
import {
  ConfirmDialog,
  CountChip,
  ErpLink,
  Initials,
  LightChip,
  Meter,
  Panel,
  SegmentBadge,
  valueText,
} from './ui';

/** Claves de un caso agrupado (la principal y las de sus otras reglas), para casarlo con una hoja */
const keysOf = (a: BoardAlert) => [a.key, ...(a.related ?? []).map((r: Alert) => r.key)];

/** Mesa de la oficina y resumen de cada tándem (casos en la mesa, total y hoja) */
export function useTandemData() {
  const { board, alerts, filter } = useBoardCtx();
  return useMemo(() => {
    const table = buildTable(alerts, filter, { scoring: board.scoring });
    const tandems = summarizeTandems(board.tandems, alerts, filter, table).sort(
      (a, b) =>
        (b.inTable ?? 0) - (a.inTable ?? 0) ||
        b.alerts - a.alerts ||
        a.affName.localeCompare(b.affName, 'es'),
    );
    return { table, tandems };
  }, [alerts, filter, board.scoring, board.tandems]);
}

/* ------------------------------ Columna izquierda ------------------------------ */

function Pips({ n }: { n: number }) {
  return (
    <span className="flex gap-1" aria-label={`Hoja: ${n} de ${MAX_SHEETS_PER_TANDEM}`}>
      {Array.from({ length: MAX_SHEETS_PER_TANDEM }, (_, i) => (
        <span key={i} className={`h-1.5 w-3.5 rounded-full ${i < n ? 'bg-ink' : 'bg-band'}`} />
      ))}
    </span>
  );
}

export function TandemCards({
  selected,
  onSelect,
}: {
  selected: string | null;
  onSelect: (affId: string | null) => void;
}) {
  const { board } = useBoardCtx();
  const { table, tandems } = useTandemData();
  const sheetsBy = (affId: string) => board.tandemSheets.filter((s) => s.affId === affId).length;
  const anySheet = board.tandemSheets.length > 0;
  const base = 'tandem-entry w-full min-w-0 rounded-2xl border p-3 text-left transition-colors';
  const on = 'border-orange bg-orange-soft/50';
  const off = 'border-line bg-card hover:bg-card-2';

  return (
    <nav aria-label="Tándems" className="tandem-list grid grid-cols-[minmax(0,1fr)] gap-2">
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={`${base} ${selected == null ? on : off}`}
        aria-pressed={selected == null}
      >
        <div className="flex items-center gap-3">
          <span className="inline-grid size-9 shrink-0 place-items-center rounded-full bg-ink text-paper">
            <Building2 className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-ink">Toda la oficina</div>
            <div className="text-xs text-ink-3">La mesa · {board.tandems.length} tándems</div>
          </div>
        </div>
        <div className="mt-2 flex gap-1.5">
          <CountChip icon={Presentation} value={table.inside.length} label="Casos en la mesa" tone="accent" />
          <CountChip
            icon={TriangleAlert}
            value={table.inside.length + table.outside.length}
            label="Casos fuera de umbral (total)"
          />
        </div>
      </button>

      {tandems.map((t) => {
        const n = sheetsBy(t.affId);
        const silent = anySheet && t.alerts > 0 && n === 0;
        return (
          <button
            key={t.affId}
            type="button"
            onClick={() => onSelect(t.affId)}
            aria-pressed={selected === t.affId}
            data-silent={silent || undefined}
            className={`${base} ${selected === t.affId ? on : silent ? 'border-bad bg-bad-soft/60 hover:bg-bad-soft' : off}`}
          >
            <div className="flex items-center gap-3">
              <Initials name={t.affName} tone={silent ? 'bad' : 'default'} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-ink">{t.affName}</div>
                <div className="truncate text-xs text-ink-3">
                  {t.coordinatorName ? `con ${t.coordinatorName} · ` : ''}
                  {t.communities} CCPP
                </div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-1.5">
                <CountChip
                  icon={Presentation}
                  value={t.inTable ?? 0}
                  label="Casos en la mesa"
                  tone="accent"
                />
                <CountChip icon={TriangleAlert} value={t.alerts} label="Casos fuera de umbral (total)" />
              </div>
              {t.alerts === 0 && n === 0 ? (
                <span className="text-xs text-ink-3">Sin casos</span>
              ) : silent ? (
                <Chip size="sm" variant="soft" color="danger">
                  <Chip.Label>Silencioso</Chip.Label>
                </Chip>
              ) : (
                <span className="flex items-center gap-1.5 text-xs text-ink-2">
                  <ClipboardList className="size-3.5" /> {n}/{MAX_SHEETS_PER_TANDEM} <Pips n={n} />
                </span>
              )}
            </div>
          </button>
        );
      })}
    </nav>
  );
}

/* ------------------------------ Espacio del tándem ------------------------------ */

const LINE_KEYS = ['quePasa', 'queHemosIntentado', 'porQueAtascado', 'queProponemos'] as const;
const filledLines = (s: TandemSheet) => LINE_KEYS.filter((k) => s[k].trim()).length;

/** Tarjeta de caso seleccionable como un checkbox (clic o espacio para marcar/desmarcar) */
function SelectableCase({
  a,
  checked,
  disabled,
  onToggle,
}: {
  a: BoardAlert;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const { rules, board, openCase } = useBoardCtx();
  const erp = useErpLinks();
  const rule = rules[a.ruleId];
  const toggle = () => !disabled && onToggle();
  return (
    <li>
      <div
        role="checkbox"
        aria-checked={checked}
        aria-disabled={disabled}
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            toggle();
          }
        }}
        className={`tandem-selectable-case group grid cursor-pointer grid-cols-[auto_1fr_auto] items-start gap-4 rounded-2xl border p-4 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-orange motion-reduce:transition-none ${
          checked
            ? 'border-orange bg-orange-soft/50 shadow-sm'
            : disabled
              ? 'cursor-not-allowed border-line bg-card opacity-50'
              : 'border-line bg-card hover:border-orange/60 hover:bg-card-2'
        }`}
      >
        <span
          aria-hidden
          className={`mt-0.5 inline-grid size-6 place-items-center rounded-full border-2 transition-colors ${
            checked
              ? 'border-orange bg-orange text-[var(--checked-fg)]'
              : 'border-line bg-card group-hover:border-orange/60'
          }`}
        >
          {checked && <Check className="size-4" strokeWidth={3} />}
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <SegmentBadge segment={a.segment} />
            <span className="font-bold text-ink">{a.caseTitle}</span>
            {a.manual && (
              <Chip size="sm" variant="soft" color="warning">
                <Chip.Label>Manual</Chip.Label>
              </Chip>
            )}
            {isMaxPriority(a, board.scoring) && (
              <Chip size="sm" variant="soft" color="accent">
                <Chip.Label>Prioridad máx.</Chip.Label>
              </Chip>
            )}
          </div>
          <div className="mt-1 text-sm text-ink-3">
            {a.communityName}
            {a.daysToMeeting != null && <> · junta en {a.daysToMeeting} d</>}
            {a.objectCode && <span className="tabular"> · {a.objectCode}</span>}
          </div>
          {a.related && a.related.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {a.related.map((r) => (
                <span key={r.key} className="rounded-full bg-card-2 px-2 py-0.5 text-xs text-ink-2">
                  también {rules[r.ruleId]?.name.toLowerCase() ?? r.ruleId} ·{' '}
                  {valueText(rules[r.ruleId], r.value)}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="grid w-36 justify-items-end gap-1.5">
          <div className="flex items-center gap-2">
            <LightChip light={a.light} />
            <span className="tabular text-lg font-extrabold text-ink">{valueText(rule, a.value)}</span>
          </div>
          {rule && !a.manual && (
            <div className="w-full">
              <Meter value={a.value} rule={a} light={a.light} />
            </div>
          )}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openCase(a);
              }}
              className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-semibold text-ink-2 hover:bg-card-2"
              title="Ver la ficha del caso"
            >
              <Eye className="size-3.5" /> Ficha
            </button>
            <ErpLink href={erp.object(a)} className="rounded-lg p-1 hover:bg-card-2" />
          </div>
        </div>
      </div>
    </li>
  );
}

/** Tarjeta pequeña de un caso ya elegido: se pulsa para rellenar su hoja en la ficha */
function PickedCase({
  index,
  sheet,
  alert,
  onOpen,
  onRemove,
  showAction = false,
}: {
  index: number;
  sheet: TandemSheet;
  alert: BoardAlert | undefined;
  onOpen?: () => void;
  onRemove: () => void;
  showAction?: boolean;
}) {
  const n = filledLines(sheet);
  const done = n === LINE_KEYS.length;
  return (
    <li
      className={`tandem-picked-case relative grid min-w-0 rounded-2xl border ${done ? 'border-ok bg-ok-soft/40' : 'border-line bg-card'}`}
      data-complete={done}
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        className="tandem-picked-open grid h-full w-full min-w-0 cursor-pointer gap-2 rounded-[inherit] p-4 text-left transition-colors outline-none hover:bg-card-2/50 focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-inset disabled:cursor-default disabled:hover:bg-transparent motion-reduce:transition-none"
      >
        <div className="flex items-center gap-2">
          <span
            className={`inline-grid size-6 shrink-0 place-items-center rounded-full text-xs font-extrabold ${done ? 'bg-ok text-[var(--ok-fg)]' : 'bg-orange text-[var(--highlight-fg)]'}`}
          >
            {done ? <Check className="size-3.5" strokeWidth={3} /> : index + 1}
          </span>
          <span className="line-clamp-2 pr-4 text-sm font-bold text-ink">
            {alert?.caseTitle ?? 'Caso que ya no está fuera de umbral'}
          </span>
        </div>
        {alert && <div className="truncate text-xs text-ink-3">{alert.communityName}</div>}
        <div className="flex items-center justify-between gap-2">
          {alert ? <LightChip light={alert.light} /> : <span />}
          <span className={`text-xs font-semibold ${done ? 'text-ok' : 'text-ink-2'}`}>
            {done ? 'Hoja completa' : `${n}/4 líneas`}
          </span>
        </div>
        <div className="flex h-1 gap-0.5" aria-hidden>
          {LINE_KEYS.map((k) => (
            <span
              key={k}
              className={`flex-1 rounded-full ${sheet[k].trim() ? (done ? 'bg-ok' : 'bg-orange') : 'bg-band'}`}
            />
          ))}
        </div>
        {showAction && onOpen && (
          <span className="tandem-picked-caption flex items-center justify-between gap-2 text-xs font-semibold text-orange-ink">
            {done ? 'Revisar hoja' : 'Rellenar hoja'} <ArrowRight className="size-3.5" />
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-2 right-2 grid size-7 cursor-pointer place-items-center rounded-full text-ink-3 transition-colors outline-none hover:bg-card-2 hover:text-bad focus-visible:ring-2 focus-visible:ring-orange motion-reduce:transition-none"
        aria-label="Quitar de la hoja"
        title="Quitar de la hoja"
      >
        <X className="size-3.5" />
      </button>
    </li>
  );
}

export function TandemWorkspace({
  tandem,
  layout = 'classic',
}: {
  tandem: TandemSummary;
  layout?: DashboardLayout;
}) {
  const { board, officeId, rules, openCase } = useBoardCtx();
  const { table } = useTandemData();
  const save = useSaveTandemSheet(officeId);
  const del = useDeleteTandemSheet(officeId);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [showAllOutside, setShowAllOutside] = useState(false);
  const pickedCasesRef = useRef<HTMLOListElement>(null);

  const stableSelection = layout === 'v2';
  const storedSheets = useMemo(
    () => board.tandemSheets.filter((s) => s.affId === tandem.affId),
    [board.tandemSheets, tandem.affId],
  );
  const [caseOrder, setCaseOrder] = useState(() => storedSheets.map((s) => s.caseKey));
  useEffect(() => {
    if (!stableSelection) return;
    setCaseOrder((previous) => {
      const currentKeys = new Set(storedSheets.map((s) => s.caseKey));
      const next = [
        ...previous.filter((key) => currentKeys.has(key)),
        ...storedSheets.filter((s) => !previous.includes(s.caseKey)).map((s) => s.caseKey),
      ];
      return next.length === previous.length && next.every((key, i) => key === previous[i]) ? previous : next;
    });
  }, [storedSheets, stableSelection]);
  const sheets = stableSelection
    ? [...storedSheets].sort((a, b) => {
        const position = (key: string) => {
          const index = caseOrder.indexOf(key);
          return index < 0 ? Number.MAX_SAFE_INTEGER : index;
        };
        return position(a.caseKey) - position(b.caseKey);
      })
    : storedSheets;
  const full = sheets.length >= MAX_SHEETS_PER_TANDEM;
  // En v2, completar la selección no cambia de vista ni desplaza la lista.
  const [phase, setPhase] = useState<'select' | 'fill'>(full ? 'fill' : 'select');
  const prevCount = useRef(sheets.length);
  useEffect(() => {
    if (
      !stableSelection &&
      sheets.length >= MAX_SHEETS_PER_TANDEM &&
      prevCount.current < MAX_SHEETS_PER_TANDEM
    )
      setPhase('fill');
    if (sheets.length === 0) setPhase('select');
    prevCount.current = sheets.length;
  }, [sheets.length, stableSelection]);

  const mineIn = table.inside.filter((a) => a.affId === tandem.affId);
  const mineOut = table.outside.filter((a) => a.affId === tandem.affId);
  const byKey = new Map<string, BoardAlert>();
  for (const a of [...mineIn, ...mineOut]) for (const k of keysOf(a)) byKey.set(k, a);
  const sheetFor = (a: BoardAlert) => sheets.find((s) => keysOf(a).includes(s.caseKey));

  const match = makeMatcher(q);
  const hit = (a: BoardAlert) =>
    match(
      a.caseTitle,
      a.communityName,
      a.objectCode,
      a.affName,
      rules[a.ruleId]?.name,
      ...(a.related ?? []).map((r) => rules[r.ruleId]?.name),
    );
  const searching = q.trim().length > 0;
  const inList = mineIn.filter(hit);
  const outList = mineOut.filter(hit);
  const OUT_LIMIT = 30;

  const toggle = (a: BoardAlert) => {
    const s = sheetFor(a);
    if (s) {
      removeSheet(s);
    } else {
      save.mutate(
        {
          week: board.week,
          affId: tandem.affId,
          caseKey: a.key,
          quePasa: '',
          queHemosIntentado: '',
          porQueAtascado: '',
          queProponemos: '',
        },
        { onError: (e) => toast.danger(e.message) },
      );
    }
  };
  // Si el caso ya tiene líneas escritas, se pide confirmación antes de quitarlo de la hoja
  const [confirmRemove, setConfirmRemove] = useState<TandemSheet | null>(null);
  const doRemove = (s: TandemSheet) =>
    del.mutate(s.id, { onSuccess: () => setConfirmRemove(null), onError: (e) => toast.danger(e.message) });
  function removeSheet(s: TandemSheet) {
    if (filledLines(s) > 0) setConfirmRemove(s);
    else doRemove(s);
  }
  const sequence = sheets.map((s) => byKey.get(s.caseKey)?.key).filter((k): k is string => !!k);
  const fillCase = (s: TandemSheet) => {
    const a = byKey.get(s.caseKey);
    if (a) openCase(a, { sequence });
  };
  const done = sheets.filter((s) => filledLines(s) === LINE_KEYS.length).length;
  const beginFilling = () => {
    setPhase('fill');
    if (stableSelection) {
      pickedCasesRef.current?.querySelector<HTMLButtonElement>('.tandem-picked-open:enabled')?.focus();
    }
  };

  const header = (
    <div className="flex flex-wrap items-center gap-2">
      <Button size="sm" variant="secondary" onPress={() => setAdding(true)}>
        <Plus className="size-4" /> Añadir caso
      </Button>
      {phase === 'select' && (stableSelection || sheets.length > 0) && (
        <Button size="sm" isDisabled={sheets.length === 0} onPress={beginFilling}>
          Rellenar hojas ({sheets.length}) <ArrowRight className="size-4" />
        </Button>
      )}
      {phase === 'fill' && (
        <Button size="sm" variant="ghost" onPress={() => setPhase('select')}>
          <Pencil className="size-4" /> Cambiar selección
        </Button>
      )}
    </div>
  );

  return (
    <div className="grid gap-6">
      <Panel
        title={phase === 'select' ? `Elige los casos de ${tandem.affName}` : `Hoja de ${tandem.affName}`}
        description={
          phase === 'select'
            ? `Hasta ${MAX_SHEETS_PER_TANDEM} casos para la reunión · semana ${board.week.slice(-2)}`
            : `${done} de ${sheets.length} hojas completas · pulsa un caso para rellenarlo`
        }
        action={header}
      >
        {phase === 'select' && !stableSelection ? (
          <div className="flex flex-wrap items-center gap-3">
            <div
              className="flex gap-1.5"
              aria-label={`${sheets.length} de ${MAX_SHEETS_PER_TANDEM} elegidos`}
            >
              {Array.from({ length: MAX_SHEETS_PER_TANDEM }, (_, i) => {
                const s = sheets[i];
                const a = s ? byKey.get(s.caseKey) : undefined;
                return (
                  <span
                    key={i}
                    className={`inline-flex h-8 max-w-56 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold ${
                      s
                        ? 'border-orange bg-orange-soft text-orange-ink'
                        : 'border-dashed border-line text-ink-3'
                    }`}
                  >
                    {s ? (
                      <>
                        <Check className="size-3.5 shrink-0" strokeWidth={3} />
                        <span className="truncate">{a?.caseTitle ?? 'Caso'}</span>
                      </>
                    ) : (
                      `Caso ${i + 1}`
                    )}
                  </span>
                );
              })}
            </div>
            <span className="tabular text-sm font-bold text-ink">
              {sheets.length}/{MAX_SHEETS_PER_TANDEM}
            </span>
          </div>
        ) : (
          <ol
            ref={pickedCasesRef}
            className="tandem-picked-list grid gap-3 md:grid-cols-3"
            aria-label="Casos elegidos para la reunión"
          >
            {sheets.map((s, i) => (
              <PickedCase
                key={s.id}
                index={i}
                sheet={s}
                alert={byKey.get(s.caseKey)}
                onOpen={byKey.get(s.caseKey) ? () => fillCase(s) : undefined}
                onRemove={() => removeSheet(s)}
                showAction={stableSelection}
              />
            ))}
            {Array.from({ length: MAX_SHEETS_PER_TANDEM - sheets.length }, (_, i) => (
              <li key={`empty-${i}`}>
                {stableSelection && phase === 'select' ? (
                  <div className="tandem-picked-empty grid h-full min-h-28 content-center justify-items-center gap-2 rounded-2xl border border-dashed border-line px-4 text-center text-sm text-ink-3">
                    <span className="grid size-6 place-items-center rounded-full bg-card-2 text-xs font-semibold">
                      {sheets.length + i + 1}
                    </span>
                    Elige un caso de la lista
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPhase('select')}
                    className="tandem-picked-empty grid h-full min-h-28 w-full cursor-pointer place-items-center rounded-2xl border border-dashed border-line text-sm text-ink-3 transition-colors outline-none hover:border-orange/60 hover:bg-card-2 focus-visible:ring-2 focus-visible:ring-orange motion-reduce:transition-none"
                  >
                    <span className="flex items-center gap-1.5">
                      <Plus className="size-4" /> Elegir otro caso
                    </span>
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
        {stableSelection && (
          <div
            className="tandem-selection-status mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-2"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="font-semibold tabular text-ink">
              {sheets.length} de {MAX_SHEETS_PER_TANDEM} casos elegidos
            </span>
            <span>
              {phase === 'fill'
                ? 'Abre una tarjeta para rellenar o revisar su hoja.'
                : full
                  ? 'Selección lista. Puedes cambiarla o continuar con las hojas.'
                  : 'Selecciona hasta tres casos; puedes abrir sus hojas en cualquier momento.'}
            </span>
          </div>
        )}
      </Panel>

      {phase === 'select' && (
        <Panel
          title="Casos del tándem"
          description={`${mineIn.length} en la mesa · ${mineOut.length} fuera de la mesa`}
          action={
            <Input
              aria-label="Buscar caso"
              placeholder="Buscar: palabras, sin tildes…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-64"
            />
          }
        >
          <div className="grid gap-5">
            <section>
              <div className="mb-2 flex items-center gap-2">
                <Presentation className="size-4 text-orange-ink" />
                <span className="text-xs font-bold tracking-wider text-orange-ink uppercase">En la mesa</span>
                <span className="text-xs text-ink-3">{inList.length}</span>
              </div>
              {inList.length ? (
                <ul className="grid gap-2">
                  {inList.map((a) => (
                    <SelectableCase
                      key={a.key}
                      a={a}
                      checked={!!sheetFor(a)}
                      disabled={full && !sheetFor(a)}
                      onToggle={() => toggle(a)}
                    />
                  ))}
                </ul>
              ) : (
                <p className="py-2 text-sm text-ink-3">
                  {searching
                    ? 'Ningún caso coincide con la búsqueda.'
                    : 'Ningún caso de este tándem entra en la mesa.'}
                </p>
              )}
            </section>
            <section>
              <div className="mb-2 flex items-center gap-2">
                <TriangleAlert className="size-4 text-ink-3" />
                <span className="text-xs font-bold tracking-wider text-ink-2 uppercase">
                  Fuera de la mesa
                </span>
                <span className="text-xs text-ink-3">{outList.length}</span>
              </div>
              {outList.length ? (
                <>
                  <ul className="grid gap-2">
                    {(showAllOutside || searching ? outList : outList.slice(0, OUT_LIMIT)).map((a) => (
                      <SelectableCase
                        key={a.key}
                        a={a}
                        checked={!!sheetFor(a)}
                        disabled={full && !sheetFor(a)}
                        onToggle={() => toggle(a)}
                      />
                    ))}
                  </ul>
                  {!searching && !showAllOutside && outList.length > OUT_LIMIT && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-2"
                      onPress={() => setShowAllOutside(true)}
                    >
                      Ver los {outList.length} casos
                    </Button>
                  )}
                </>
              ) : (
                <p className="py-2 text-sm text-ink-3">
                  {searching ? 'Ningún caso coincide con la búsqueda.' : 'No hay más casos fuera de umbral.'}
                </p>
              )}
            </section>
            <div className="flex items-center gap-2 rounded-xl bg-card-2 px-3 py-2 text-xs text-ink-2">
              <Search className="size-3.5" /> ¿No está el caso que trae el tándem? Regístralo con «Añadir
              caso».
            </div>
            {stableSelection && (
              <div className="tandem-selection-footer flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
                <span className="text-sm text-ink-2">
                  {sheets.length} de {MAX_SHEETS_PER_TANDEM} casos elegidos para la reunión
                </span>
                <Button size="sm" isDisabled={sheets.length === 0} onPress={beginFilling}>
                  Rellenar hojas ({sheets.length}) <ArrowRight className="size-4" />
                </Button>
              </div>
            )}
          </div>
        </Panel>
      )}

      <ConfirmDialog
        isOpen={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        title="¿Quitar este caso de la hoja?"
        confirmLabel="Quitar de la hoja"
        isPending={del.isPending}
        onConfirm={() => confirmRemove && doRemove(confirmRemove)}
      >
        «{(confirmRemove && byKey.get(confirmRemove.caseKey)?.caseTitle) ?? 'Caso'}» ya tiene{' '}
        {confirmRemove ? filledLines(confirmRemove) : 0} de 4 líneas escritas. Se perderán.
      </ConfirmDialog>
      <ManualCaseSheet
        key={tandem.affId}
        isOpen={adding}
        onClose={() => setAdding(false)}
        defaultAffId={tandem.affId}
        sheetFull={full}
      />
    </div>
  );
}
